import React, { useState, useMemo } from 'react';
import { FeatherChevronUp, FeatherChevronDown } from '../ui/icons';
import { Badge } from '../ui/badge';
import ClickableFeatureTag from '../shared/ClickableFeatureTag';
import TaskDueDate from '../shared/TaskDueDate';
import TaskNotifications from '../shared/TaskNotifications';
import { getRelativeDateDescription } from '../../utils/date';
import './TaskDistributionTable.css';

const TaskDistributionTable = ({ taskDistribution, onTaskClick, variant, featureCompletionMap = {}, allFeatureTags = [], allTasks = [] }) => {
    const isReport = variant === 'report';
    const [sortBy, setSortBy] = useState('hours'); // 'taskName', 'hours'
    const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'
    const [groupByResource, setGroupByResource] = useState(false);

    const sortedTasks = useMemo(() => {
        let filtered = [...(taskDistribution || [])];

        filtered.sort((a, b) => {
            let aVal, bVal;
            switch (sortBy) {
                case 'taskName':
                    aVal = a.title?.toLowerCase() || '';
                    bVal = b.title?.toLowerCase() || '';
                    break;
                case 'hours':
                    aVal = a.totalHours || 0;
                    bVal = b.totalHours || 0;
                    break;
                default:
                    return 0;
            }

            if (sortOrder === 'asc') {
                return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
            } else {
                return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
            }
        });

        return filtered;
    }, [taskDistribution, sortBy, sortOrder]);

    // Group tasks by resource
    const tasksByResource = useMemo(() => {
        if (!groupByResource || !taskDistribution) return null;

        const resourceMap = new Map();

        taskDistribution.forEach(task => {
            task.resources?.forEach(resource => {
                if (!resourceMap.has(resource.userId)) {
                    resourceMap.set(resource.userId, {
                        userId: resource.userId,
                        name: resource.name,
                        email: resource.email,
                        tasks: [],
                        totalHours: 0
                    });
                }

                const resourceGroup = resourceMap.get(resource.userId);
                resourceGroup.tasks.push({
                    ...task,
                    resourceHours: resource.hours || 0,
                    resourceName: resource.name
                });
                resourceGroup.totalHours += parseFloat(resource.hours) || 0;
            });
        });

        // Sort resources by total hours (descending)
        const resources = Array.from(resourceMap.values());
        resources.sort((a, b) => b.totalHours - a.totalHours);

        // Sort tasks within each resource by hours (descending)
        resources.forEach(resource => {
            resource.tasks.sort((a, b) => b.resourceHours - a.resourceHours);
        });

        return resources;
    }, [taskDistribution, groupByResource]);

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('desc');
        }
    };

    const SortIcon = ({ column }) => {
        if (sortBy !== column) return null;
        return sortOrder === 'asc' ? <FeatherChevronUp /> : <FeatherChevronDown />;
    };

    const getStatusVariant = (status) => {
        switch (status?.toLowerCase()) {
            case 'completed':
                return 'success';
            case 'in-progress':
                return 'warning';
            case 'open':
                return 'secondary';
            case 'closed':
            case 'released':
                return 'info';
            default:
                return 'secondary';
        }
    };

    if (!taskDistribution || taskDistribution.length === 0) {
        return (
            <div className="task-distribution-section">
                <div className="section-header">
                    <h2>Task Distribution by Resource</h2>
                </div>
                <div className="empty-state">
                    <p>No tasks picked up for this date.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`task-distribution-section ${isReport ? 'task-distribution-section-report' : ''}`}>
            <div className="section-header">
                <h2>Task Distribution by Resource</h2>
                {!isReport && (
                    <label className="group-by-resource-toggle">
                        <span className="toggle-label">Group by Resource</span>
                        <div 
                            className={`toggle-switch ${groupByResource ? 'active' : ''}`}
                            onClick={() => setGroupByResource(!groupByResource)}
                        >
                            <div className="toggle-slider"></div>
                        </div>
                    </label>
                )}
            </div>
            {groupByResource && tasksByResource ? (
                <div className="grouped-by-resource-container">
                    {tasksByResource.map((resource) => (
                        <div key={resource.userId} className="resource-group">
                            <div className="resource-group-header">
                                <div className="resource-group-info">
                                    <span className="resource-group-name">{resource.name}</span>
                                    <span className="resource-group-meta">
                                        {resource.tasks.length} {resource.tasks.length === 1 ? 'task' : 'tasks'} • {resource.totalHours.toFixed(1)}h
                                    </span>
                                </div>
                            </div>
                            <div className="table-container">
                                <table className="task-distribution-table">
                                    <thead>
                                        <tr>
                                            <th>Task</th>
                                            <th>Status</th>
                                            <th>Hours</th>
                                            <th>Last worked on</th>
                                            <th>Activity</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {resource.tasks.map((task) => (
                                            <tr key={`${resource.userId}-${task.taskId}`}>
                                                <td>
                                                    <div 
                                                        className={`task-info-dist ${onTaskClick ? 'task-info-dist-clickable' : ''}`}
                                                        onClick={onTaskClick ? () => onTaskClick({
                                                            id: task.taskId,
                                                            title: task.title,
                                                            display_id: task.displayId,
                                                            description: task.description,
                                                            status: task.status,
                                                            feature_tag: task.feature_tag,
                                                            notification: task.notification,
                                                            created_at: task.created_at,
                                                            updated_at: task.updated_at
                                                        }) : undefined}
                                                    >
                                                        <div className="task-title-row">
                                                            {task.displayId && (
                                                                <span className="task-id-bracket">[{task.displayId}]</span>
                                                            )}
                                                            <div className="task-title">
                                                                {task.title}
                                                            </div>
                                                            {task.feature_tag && (
                                                                <>
                                                                    <ClickableFeatureTag
                                                                        featureTag={task.feature_tag}
                                                                        allFeatureTags={allFeatureTags}
                                                                        allTasks={allTasks}
                                                                        size="small"
                                                                    />
                                                                    {task.feature_tag.due_date && 
                                                                     featureCompletionMap[task.feature_tag.id] !== 100 && (
                                                                        <TaskDueDate dueDate={task.feature_tag.due_date} />
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <Badge variant={getStatusVariant(task.status)}>
                                                        {task.status || 'N/A'}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <span className="resource-hours-display">
                                                        {task.resourceHours.toFixed(1)}h
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="last-worked-on">
                                                        {task.lastWorkPlanDate ? getRelativeDateDescription(task.lastWorkPlanDate) : 'Never'}
                                                    </span>
                                                </td>
                                                <td>
                                                    <TaskNotifications
                                                        taskId={task.taskId}
                                                        taskTitle={task.title}
                                                        notification={task.notification}
                                                        isLoading={false}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="table-container">
                    <table className="task-distribution-table">
                        <thead>
                            <tr>
                                <th 
                                    onClick={!isReport ? () => handleSort('taskName') : undefined} 
                                    className={!isReport ? "sortable" : ""}
                                >
                                    <div className="th-content">
                                        Task
                                        {!isReport && <SortIcon column="taskName" />}
                                    </div>
                                </th>
                                <th>Status</th>
                                <th>Resource</th>
                                <th>Last worked on</th>
                                <th>Activity</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedTasks.map((task) => (
                                <tr key={task.taskId}>
                                    <td>
                                        <div 
                                            className={`task-info-dist ${!isReport && onTaskClick ? 'task-info-dist-clickable' : ''}`}
                                            onClick={!isReport && onTaskClick ? () => onTaskClick({
                                                id: task.taskId,
                                                title: task.title,
                                                display_id: task.displayId,
                                                description: task.description,
                                                status: task.status,
                                                feature_tag: task.feature_tag,
                                                notification: task.notification,
                                                created_at: task.created_at,
                                                updated_at: task.updated_at
                                            }) : undefined}
                                        >
                                            <div className="task-title-row">
                                                {task.displayId && (
                                                    <span className="task-id-bracket">[{task.displayId}]</span>
                                                )}
                                                <div className="task-title">
                                                    {task.title}
                                                </div>
                                                {task.feature_tag && (
                                                    <>
                                                        <ClickableFeatureTag
                                                            featureTag={task.feature_tag}
                                                            allFeatureTags={allFeatureTags}
                                                            allTasks={allTasks}
                                                            size="small"
                                                        />
                                                        {task.feature_tag.due_date && 
                                                         featureCompletionMap[task.feature_tag.id] !== 100 && (
                                                            <TaskDueDate dueDate={task.feature_tag.due_date} />
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <Badge variant={getStatusVariant(task.status)}>
                                            {task.status || 'N/A'}
                                        </Badge>
                                    </td>
                                    <td>
                                        <div className="resources-cell">
                                            <div className="resource-chips">
                                                {task.resources?.map((resource, idx) => (
                                                    <span key={idx} className="resource-chip">
                                                        {resource.name}
                                                        {resource.hours > 0 && (
                                                            <span className="resource-hours">
                                                                ({resource.hours}h)
                                                            </span>
                                                        )}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className="last-worked-on">
                                            {task.lastWorkPlanDate ? getRelativeDateDescription(task.lastWorkPlanDate) : 'Never'}
                                        </span>
                                    </td>
                                    <td>
                                        <TaskNotifications
                                            taskId={task.taskId}
                                            taskTitle={task.title}
                                            notification={task.notification}
                                            isLoading={false}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default TaskDistributionTable;

